import { createContext, useCallback, useContext, useEffect, useState, type ReactNode, createElement } from 'react'

interface DirtyContextValue {
  isDirty: (scope: string) => boolean
  setDirty: (scope: string, value: boolean) => void
  clearScope: (scope: string) => void
  anyDirty: boolean
}

export const DirtyContext = createContext<DirtyContextValue | null>(null)

export function DirtyProvider({ children }: { children: ReactNode }) {
  const [scopes, setScopes] = useState<Record<string, boolean>>({})

  const isDirty = useCallback((scope: string) => scopes[scope] ?? false, [scopes])

  const setDirty = useCallback((scope: string, value: boolean) => {
    setScopes(prev => ({ ...prev, [scope]: value }))
  }, [])

  const clearScope = useCallback((scope: string) => {
    setScopes(prev => {
      const next = { ...prev }
      delete next[scope]
      return next
    })
  }, [])

  const anyDirty = Object.values(scopes).some(Boolean)

  return createElement(DirtyContext.Provider, { value: { isDirty, setDirty, clearScope, anyDirty } }, children)
}

export function useDirty(scope: string) {
  const ctx = useContext(DirtyContext)
  if (!ctx) throw new Error('useDirty must be used within DirtyProvider')

  const { isDirty, setDirty, clearScope, anyDirty } = ctx

  useEffect(() => {
    return () => clearScope(scope)
  }, [scope, clearScope])

  return {
    dirty: isDirty(scope),
    setDirty: useCallback((value: boolean) => setDirty(scope, value), [scope, setDirty]),
    clearDirty: useCallback(() => clearScope(scope), [scope, clearScope]),
    anyDirty,
  }
}
